import { useModals } from 'almanac-ui/Modal'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { AiErrorModal } from 'components/AiErrorModal/AiErrorModal'
import {
  createDocGenerationRequest,
  GenerationRequestProps,
  GenerationType,
} from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { layersKeys } from 'hooks/queries/layers/layersKeys'
import { useCloseLeftSidebar } from 'hooks/useCloseLeftSidebar'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { DOC_PATH, FOLDER_PATH } from 'paths'
import { Selection } from 'prosemirror-state'
import { useMutation, useQueryClient } from 'react-query'
import { generatePath, useHistory } from 'react-router-dom'
import { useSourceMaterials } from 'hooks/useSourceMaterials'
import { AlmanacFile, GeneratePrompt } from 'types'
import { reportError } from 'utils/error'
import { createAISuggestionNode, Data } from 'hooks/queries/layers/aiSuggestionNodes/useCreateAISuggestionNode'
import { DesignChannel } from 'components/NewFileModal/Types'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'

interface Props {
  generationType: ChannelType | GenerationType
  articleId?: string
  layerId?: string
  projectId?: string
  nodeSelection?: Selection
  promptLibrary?: string
  isTurnInto?: boolean
  onSuccess?: (data: { almanac_file: AlmanacFile } | Data) => void
  navigateToDocOnComplete?: boolean
}

export const useGenerateContent = ({
  generationType,
  articleId,
  layerId,
  nodeSelection,
  isTurnInto,
  onSuccess,
  promptLibrary = 'default',
  navigateToDocOnComplete = true,
}: Props) => {
  const workspaceId = useCurrentWorkspaceId()
  const { openModal, closeModal, closeAllModals } = useModals()
  const history = useHistory()
  const queryClient = useQueryClient()
  const { isMobile } = useResponsiveDesign()
  const { closeLeftSidebar } = useCloseLeftSidebar()

  const generateDocMutation = useMutation(
    async (payload: GenerationRequestProps) => {
      const { articleId: payloadArticleId } = { articleId, ...payload }
      const response =
        payloadArticleId && layerId
          ? await createAISuggestionNode({
              ...payload,
              layerId,
              articleId: payloadArticleId,
              selection: { type: 'all' },
            })
          : await createDocGenerationRequest(payload)
      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error()
      }
      return response.data
    },
    {
      onSuccess() {
        if (isMobile) {
          closeLeftSidebar()
        }
        if (navigateToDocOnComplete) {
          closeAllModals()
        }
        if (articleId) {
          queryClient.invalidateQueries([layersKeys.prefix, articleId, 'list'])
        }
      },
    },
  )
  const sourceMaterials = useSourceMaterials()
  const generateContent = (
    values: Partial<GeneratePrompt & { filename?: string; project?: AlmanacFile; articleId?: string | null }>,
  ) => {
    const { filename, project, source_material_ids, cross_post_accounts, ...prompt } = values

    const firstAccount = cross_post_accounts?.find(
      (account) => account.provider === providerFromChannelType[generationType as DesignChannel | ChannelType],
    )
    const otherAccounts = cross_post_accounts?.filter(
      (account) => account.external_integration_id !== firstAccount?.external_integration_id,
    )

    const referenceText = (sourceMaterials.data || [])
      .filter(({ id }) => values.source_material_ids?.includes(id))
      .map(({ content }) => content)
      .join('\n')

    let payload: GenerationRequestProps = {
      workspaceId,
      prompt: {
        ...prompt,
        generation_type: generationType,
        external_integration_id: firstAccount?.external_integration_id,
        cross_post_accounts: otherAccounts,
        reference_text: referenceText.length > 0 ? referenceText : undefined,
      },
      promptLibrary,
      isTurnInto,
    }

    const { articleId: payloadArticleId } = { articleId, ...values }

    if (!payloadArticleId) {
      payload = {
        ...payload,
        title: values.filename,
        crossPostAccounts: otherAccounts,
        projectId: values.project?.id,
        articleId: payloadArticleId,
      }
    } else {
      payload = {
        ...payload,
        articleId,
        mode: 'replace',
        selection: nodeSelection?.toJSON(),
      }
    }

    return generateDocMutation.mutateAsync(payload, {
      onSuccess:
        onSuccess ||
        ((data) => {
          if ('almanac_file' in data && navigateToDocOnComplete) {
            if (otherAccounts?.length) {
              history.push(generatePath(FOLDER_PATH, { folderSlug: data.almanac_file.parent?.seo_string as string }))
            } else {
              if (data.almanac_file.slug) {
                history.push(generatePath(DOC_PATH, { slug: data.almanac_file.slug }))
              } else {
                reportError('Could not redirect to doc')
              }
            }
          }
        }),
      onError(error) {
        openModal(AiErrorModal, {
          onCancel: () => {
            closeModal()
          },
          onTryAgain: () => {
            closeModal()
            generateContent(values)
          },
        })
        reportError(error)
      },
    })
  }

  return { generateContent, generateDocMutation }
}
